import { icon } from "./ui";

export interface WheelPickerColumn {
  id: string;
  label: string;
  values: readonly number[];
  value: number;
  format?: (value: number) => string;
}

export interface WheelPickerOptions {
  title: string;
  trigger: HTMLElement;
  columns: WheelPickerColumn[];
  onConfirm: (values: Record<string, number>) => void | Promise<void>;
}

export interface WheelPickerHandle {
  dialog: HTMLDialogElement;
  close(): void;
}

const rowHeight = 48;
let activePicker: WheelPickerHandle | null = null;
let pickerSequence = 0;

function boundedIndex(index: number, length: number): number {
  return Math.min(Math.max(index, 0), Math.max(0, length - 1));
}

function prefersReducedMotion(): boolean {
  return matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function openWheelPicker(options: WheelPickerOptions): WheelPickerHandle {
  activePicker?.close();

  const dialog = document.createElement("dialog");
  dialog.className = "ui-wheel-dialog";
  dialog.setAttribute("aria-label", options.title);
  const panel = document.createElement("div");
  panel.className = "ui-wheel-panel";

  const header = document.createElement("header");
  header.className = "ui-wheel-header";
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "ui-button ui-button-quiet ui-button-small";
  cancel.textContent = "Cancel";
  const title = document.createElement("strong");
  title.id = `ui-wheel-title-${++pickerSequence}`;
  title.textContent = options.title;
  dialog.setAttribute("aria-labelledby", title.id);
  const done = document.createElement("button");
  done.type = "button";
  done.className = "ui-button ui-button-accent ui-button-small";
  done.append(document.createTextNode("Done"), icon("check"));
  header.append(cancel, title, done);

  const wheels = document.createElement("div");
  wheels.className = "ui-wheel-columns";
  const error = document.createElement("p");
  error.className = "ui-wheel-error";
  error.setAttribute("role", "status");
  error.setAttribute("aria-live", "polite");
  const selected = new Map<string, number>();

  const scrollTo = (wheel: HTMLElement, index: number, smooth = true): void => {
    wheel.scrollTo({
      top: boundedIndex(index, Number(wheel.dataset.count)) * rowHeight,
      behavior: smooth && !prefersReducedMotion() ? "smooth" : "instant",
    });
  };

  for (const column of options.columns) {
    if (!column.values.length) throw new Error(`Wheel column ${column.id} has no values.`);
    const shell = document.createElement("div");
    shell.className = "ui-wheel-column-shell";
    const label = document.createElement("span");
    label.className = "ui-wheel-label";
    label.id = `${title.id}-${column.id}`;
    label.textContent = column.label;
    const wheel = document.createElement("div");
    wheel.className = "ui-wheel-column";
    wheel.tabIndex = 0;
    wheel.setAttribute("role", "spinbutton");
    wheel.setAttribute("aria-labelledby", label.id);
    wheel.setAttribute("aria-valuemin", String(column.values[0]));
    wheel.setAttribute("aria-valuemax", String(column.values.at(-1)));
    wheel.dataset.count = String(column.values.length);
    let index = Math.max(0, column.values.indexOf(column.value));

    const syncSelection = (nextIndex: number): void => {
      index = boundedIndex(nextIndex, column.values.length);
      const value = column.values[index]!;
      const valueText = column.format?.(value) ?? String(value);
      selected.set(column.id, value);
      wheel.setAttribute("aria-valuenow", String(value));
      wheel.setAttribute("aria-valuetext", valueText);
      [...wheel.children].forEach((item, itemIndex) => {
        (item as HTMLElement).dataset.selected = String(itemIndex === index);
      });
    };

    column.values.forEach((value, valueIndex) => {
      const item = document.createElement("div");
      item.className = "ui-wheel-option";
      item.textContent = column.format?.(value) ?? String(value);
      item.setAttribute("aria-hidden", "true");
      item.addEventListener("click", () => {
        syncSelection(valueIndex);
        scrollTo(wheel, valueIndex, false);
      });
      wheel.append(item);
    });
    wheel.addEventListener("scroll", () => {
      const nextIndex = boundedIndex(Math.round(wheel.scrollTop / rowHeight), column.values.length);
      if (nextIndex !== index) syncSelection(nextIndex);
    }, { passive: true });
    wheel.addEventListener("keydown", (event) => {
      const movements: Partial<Record<string, number>> = {
        ArrowUp: 1,
        ArrowDown: -1,
        PageUp: 5,
        PageDown: -5,
      };
      let nextIndex = index;
      if (event.key === "Home") nextIndex = 0;
      else if (event.key === "End") nextIndex = column.values.length - 1;
      else if (movements[event.key]) nextIndex += movements[event.key]!;
      else return;
      event.preventDefault();
      syncSelection(nextIndex);
      scrollTo(wheel, index, false);
    });
    syncSelection(index);
    shell.append(wheel, label);
    wheels.append(shell);
    requestAnimationFrame(() => scrollTo(wheel, index, false));
  }

  panel.append(header, wheels, error);
  dialog.append(panel);
  document.body.append(dialog);

  const mobile = matchMedia("(max-width: 760px)").matches;
  dialog.dataset.placement = mobile ? "sheet" : "popover";
  if (!mobile) {
    const rect = options.trigger.getBoundingClientRect();
    const width = 370;
    const estimatedHeight = 342;
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
    const below = rect.bottom + 8;
    const top = below + estimatedHeight <= window.innerHeight
      ? below
      : Math.max(12, rect.top - estimatedHeight - 8);
    dialog.style.setProperty("--ui-wheel-left", `${left}px`);
    dialog.style.setProperty("--ui-wheel-top", `${top}px`);
  }

  let removed = false;
  const close = (): void => {
    if (dialog.open) dialog.close();
    else if (!removed) {
      removed = true;
      dialog.remove();
    }
  };
  const handle = { dialog, close };
  activePicker = handle;
  dialog.addEventListener("close", () => {
    removed = true;
    dialog.remove();
    if (activePicker === handle) activePicker = null;
    requestAnimationFrame(() => { if (options.trigger.isConnected) options.trigger.focus(); });
  }, { once: true });
  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    close();
  });
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) close();
  });
  cancel.addEventListener("click", close);
  done.addEventListener("click", async () => {
    done.disabled = true;
    error.textContent = "";
    try {
      await options.onConfirm(Object.fromEntries(selected));
      close();
    } catch (caught) {
      error.textContent = caught instanceof Error ? caught.message : "Could not apply this value.";
      done.disabled = false;
    }
  });

  dialog.showModal();
  requestAnimationFrame(() => wheels.querySelector<HTMLElement>(".ui-wheel-column")?.focus());
  return handle;
}
